import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { useCreateTasks } from '../api';

import { EntitySelector } from './entity-selector';

const createTaskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  entityType: z.enum(['item', 'building', 'floor', 'room']),
  entityIds: z.array(z.string().min(1)).min(1, 'At least one entity is required'),
});

export type CreateTaskForm = z.infer<typeof createTaskSchema>;

export interface GlobalCreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
}

export const GlobalCreateTaskDialog = ({ open, onClose }: GlobalCreateTaskDialogProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CreateTaskForm>({
    resolver: zodResolver(createTaskSchema),
    defaultValues: {
      entityType: 'item',
      entityIds: [],
    },
  });

  const { reset } = form;
  const entityCount = form.watch('entityIds').length;

  const createTasksMutation = useCreateTasks();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateTaskForm) => {
    setIsSubmitting(true);
    try {
      await createTasksMutation.mutateAsync({
        title: data.title,
        description: data.description,
        entityIds: data.entityIds,
        entityType: data.entityType,
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('tasks.create.title')}</DialogTitle>
      <FormContainer formContext={form} onSuccess={onSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <EntitySelector />

            {/* Task Title */}
            <Box>
              <TextFieldElement
                name="title"
                label={t('tasks.create.titleLabel')}
                fullWidth
                required
                disabled={isSubmitting}
                helperText={
                  entityCount > 0
                    ? t('tasks.create.titleHelper', { count: entityCount })
                    : undefined
                }
              />
            </Box>

            {/* Task Description */}
            <TextFieldElement
              name="description"
              label={t('tasks.create.descriptionLabel')}
              fullWidth
              multiline
              rows={4}
              required
              disabled={isSubmitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !form.formState.isValid}
          >
            {t('tasks.create.submitButton', { count: entityCount || 1 })}
          </Button>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
};
